import errorWarning from "$assets/icons/error-warning.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { TextField } from "$components/rhf-fields";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { object, string } from "yup";
import useVoidInvoice from "./hooks/useVoidInvoice";

const REASON = "reason";

const validationSchema = object().shape({
  [REASON]: string().required(),
});

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
  label: {
    fontSize: "16px",
    color: theme.palette.grey[800],
    [theme.breakpoints.down("xs")]: {
      fontSize: "10px",
    },
  },
}));

export const VoidInvoiceModal: FC<{
  invoiceId: string;
  open: boolean;
  onClose: () => void;
}> = ({ invoiceId, open, onClose }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const formMethods = useForm<{ reason: string }>({
    mode: "onTouched",

    resolver: yupResolver(validationSchema),
  });

  const { voidUnpaidInvoice } = useVoidInvoice(invoiceId);
  const [busy, setBusy] = useState(false);

  const handleVoid = async (values: { reason: string }) => {
    setBusy(true);
    try {
      await voidUnpaidInvoice(values.reason);
    } finally {
      onClose();
    }
  };

  const reasonLabel = formatMessage({
    id: "invoicing.void.reason.label",
    defaultMessage:
      "Please note reason for voiding invoice (max 150 characters)",
  });

  const reasonPlaceholder = formatMessage({
    id: "invoicing.void.reason.placeholder",
    defaultMessage: "Reason for voiding the invoice",
  });

  const handleErrorMessage = (value) =>
    value?.id ? formatMessage(value) : value;

  return (
    <DialogContainer open={open}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={errorWarning}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="invoicing.void.modal.title"
              defaultMessage="You are about to void this invoice"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <FormProvider {...formMethods}>
        <form
          name="void-invoice-form"
          onSubmit={formMethods.handleSubmit(handleVoid)}
        >
          <DialogBody>
            <Grid container>
              <Typography variant="subtitle1">
                <FormattedMessage
                  id="invoicing.void.modal.subtitle"
                  defaultMessage="This process is not reversible. Are you sure you want to proceed?"
                />
              </Typography>
              <Typography variant="body2">
                <FormattedMessage
                  id="invoicing.void.modal.linkedOrdersNote"
                  defaultMessage="All orders linked to this invoice will move to Exempt so service delivery is not blocked."
                />
              </Typography>
              <Grid item xs={12}>
                <TextField
                  name={REASON}
                  label={reasonLabel}
                  placeholder={reasonPlaceholder}
                  disabled={busy}
                  required
                  multiline
                  minRows={5}
                  fullWidth
                  helperText={
                    formMethods.formState.touchedFields[REASON] &&
                    handleErrorMessage(formMethods.formState.errors[REASON])
                  }
                />
              </Grid>
            </Grid>
          </DialogBody>
          <DialogFooter>
            <Button color="secondary" onClick={() => onClose()} disabled={busy}>
              <FormattedMessage
                id="invoicing.void.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              type="submit"
              color="error"
              data-testid="invoicing.void.accept"
              startIcon={<CheckIcon />}
              disabled={
                formMethods.formState.isSubmitting ||
                !formMethods.formState.isValid
              }
              loading={busy}
            >
              <FormattedMessage
                id="invoicing.void.confirm"
                defaultMessage="Void Invoice"
              />
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};

export default VoidInvoiceModal;
